import { Component, inject, ChangeDetectionStrategy } from "@angular/core";
import { map, Observable, startWith, switchMap } from "rxjs";
import { SkogsvardAtgardsGrupp } from "~app/common/models/constants";
import { ApiClientService } from "~app/common/services/api-client.service";
import { Alert, GetVirkeskopAlertsQueryResult } from "~app/features/virkeskop/services/virkeskop.service";
import { DashboardService } from "../../dashboard.service";
import { AlertItem, AlertListComponent } from "../alert-list/alert-list.component";
import { MessageScopes } from "../alert-list/message-scopes";

const mapSkogsvardAlertsQueryResultToAlertItems = (result: GetVirkeskopAlertsQueryResult): AlertItem[] => result.alerts.map(alert => ({
    title: alert.title,
    message: alert.message,
    timestamp: alert.occured ? new Date(alert.occured) : null,
    severity: alert.severity,
    cssClass: mapAtgardsGruppToCssClass(alert),
    links: [
        {
            text: alert.subEntityLinkName,
            resource: `virkeskop/avtal/${result.virkeskopsavtalId}`,
            queryParams: {
                kontraktId: alert.subEntityId?.toString()
            }
        }
    ]
}));

const mapAtgardsGruppToCssClass = (alert: Alert): string => {
    switch (alert.metadata?.atgardsgrupp) {
        case SkogsvardAtgardsGrupp.markberedning:
            return "alert-brown";
        case SkogsvardAtgardsGrupp.foryngring:
            return "alert-green";
        case SkogsvardAtgardsGrupp.rojning:
            return "alert-orange";
        case SkogsvardAtgardsGrupp.inventering:
            return "alert-blue";
        case SkogsvardAtgardsGrupp.ovrigt:
            return "alert-blue";
        default:
            return "";
    }
};

@Component({
    selector: "rask-skogsvard-lista",
    imports: [AlertListComponent],
    templateUrl: "./skogsvard-lista.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./skogsvard-lista.component.scss"
})
export class SkogsvardListaComponent {
    protected events$: Observable<AlertItem[]>;
    private readonly apiClientService = inject(ApiClientService);

    private readonly dashboardService = inject(DashboardService);

    private readonly userId$ = this.dashboardService.user$.pipe(
        map(user => user.id)
    );

    constructor() {
        this.events$ = this.userId$.pipe(
            switchMap(userId => this.getVirkeskopAlerts(userId))
        );
    }

    private getVirkeskopAlerts(userId: number): Observable<AlertItem[]> {
        return this.apiClientService.get<GetVirkeskopAlertsQueryResult[]>(`virkeskoperror/${userId}?scope=${MessageScopes.Skogsvard}`).pipe(
            map(res => res.flatMap(x => mapSkogsvardAlertsQueryResultToAlertItems(x))),
            startWith(null)
        );
    }
}
